import { createContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  EN,
  LOCALE_TAG,
  STORAGE_KEY,
  loadCatalog,
  loadedCatalog,
  readInitialLocale,
  type Catalog,
  type TranslationKey,
  type UiLocale,
} from './locales.js';
import { interpolate, lookup, splitTemplate, type Vars } from './translate.js';

export interface I18nValue {
  locale: UiLocale;
  /** BCP-47 tag for `Intl.*` — pass this to every formatter (doc 20 D5). */
  tag: string;
  setLocale(next: UiLocale): void;
  /** Translate to a string. `count` selects the plural form. */
  t(key: TranslationKey, vars?: Vars & { count?: number }): string;
  /** Translate to a node — for sentences that wrap markup around a value. */
  tn(key: TranslationKey, vars: Record<string, ReactNode | string | number>): ReactNode;
}

export const I18nContext = createContext<I18nValue | null>(null);

/**
 * Makes the whole tree translatable (doc 20 §4).
 *
 * Locale lives in React state rather than a module global so that switching it
 * re-renders every consumer in place: no reload, so an owner mid-way through a
 * form keeps their input, their scroll position and any open dialog (R4).
 */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<UiLocale>(readInitialLocale);
  // The catalogue actually in hand. Starts at whatever is already loaded —
  // English always is — so the first render never waits on a chunk, and a
  // locale whose catalogue is still in flight renders English rather than
  // blanking (doc 20 R9). Non-English users see one repaint on a cold load;
  // the alternative was 50 KB gzipped of catalogues nobody reads.
  const [catalog, setCatalog] = useState<Catalog>(() => loadedCatalog(readInitialLocale()) ?? EN);

  useEffect(() => {
    let live = true;
    void loadCatalog(locale).then((next) => {
      if (live) setCatalog(next);
    });
    return () => {
      live = false;
    };
  }, [locale]);

  useEffect(() => {
    document.documentElement.setAttribute('lang', locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      /* private mode — the choice just doesn't stick */
    }
  }, [locale]);

  const value = useMemo<I18nValue>(() => {
    const fallback = EN;

    const t = (key: TranslationKey, vars?: Vars & { count?: number }) =>
      interpolate(lookup(catalog, fallback, locale, key, vars?.count), vars);

    const tn = (key: TranslationKey, vars: Record<string, ReactNode | string | number>) => {
      const count = typeof vars.count === 'number' ? vars.count : undefined;
      const template = lookup(catalog, fallback, locale, key, count);
      return splitTemplate(template).map((part, i) =>
        'text' in part ? (
          part.text
        ) : (
          // Index keys are safe here: the parts come from a fixed template and
          // are never reordered. `display: contents` keeps the node's own
          // layout — a <strong> inside a sentence stays inline.
          <span key={i} style={{ display: 'contents' }}>{vars[part.slot] ?? `{${part.slot}}`}</span>
        ),
      );
    };

    return { locale, tag: LOCALE_TAG[locale], setLocale: setLocaleState, t, tn };
  }, [locale, catalog]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
